import React, { useCallback, useMemo, useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Toolbar from "@mui/material/Toolbar";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import { ButtonUI, KMKDrawer } from "../../../../Common";
import CollapsibleVirtulizedList from "./CollapsibleVirtulizedList";
import DSContext from "./DSContext";

const useStyles = makeStyles()((theme) => ({
  ListItemButton: {
    wordBreak: "break-all",
    padding: "4px 8px",
  },
  ListItemText: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
  },
  IconButton: {
    borderRadius: "2px",
    background: theme.palette.primary.main,
    color: "#fff",
  },
  fieldType: {
    minWidth: "100px",
  },
  heading: {
    paddingLeft: "15px",
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    wordBreak: "break-all",
    "& span": {
      fontWeight: "normal",
    },
  },
  Loader: { width: 440, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" },
  DrawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: "15px",
    paddingBottom: "0",
  },
}));

const drawerWidth = 420;

const MappingList = ({
  entities,
  currentMapping,
  saveDisabled,
  selectedMappings,
  selectedMapping,
  setSelectedMapping,
  search,
}) => {
  const { classes } = useStyles();
  const [openedList, setOpenedList] = useState(currentMapping.entity || "");

  const handleClick = useCallback((name) => {
    setOpenedList((prev) => (prev === name ? "" : name));
  }, []);

  const checkDisabled = useCallback(
    (entity, field) => selectedMappings.some((mapping) => mapping.entity === entity && mapping.field === field),
    [selectedMappings]
  );

  const handleSelect = useCallback(
    (entity, field, type, length) => {
      if (selectedMapping.entity === entity && selectedMapping.field === field) {
        setSelectedMapping({ entity: "", field: "", type: "", length: "" });
      } else {
        setSelectedMapping({ entity, field, type, length });
      }
    },
    [selectedMapping.entity, selectedMapping.field]
  );

  return entities.map(({ name, fields }) => {
    return (
      <div key={name}>
        <ListItemButton onClick={() => handleClick(name)} className={classes.ListItemButton}>
          {openedList === name ? (
            <RemoveIcon className={classes.IconButton} />
          ) : (
            <AddIcon className={classes.IconButton} />
          )}
          <ListItemText sx={{ ml: 2 }} primary={name} />
        </ListItemButton>
        <CollapsibleVirtulizedList
          currentMapping={currentMapping}
          openedList={openedList}
          name={name}
          fields={fields.filter((field) => field.name.toLowerCase().includes(search.toLowerCase()))}
          checkDisabled={checkDisabled}
          saveDisabled={saveDisabled}
          selectedMapping={selectedMapping}
          handleSelect={handleSelect}
        />
      </div>
    );
  });
};

// currentMapping = current field
const AddMapping = ({ currentMapping, addMapping, fetchENTRequiredInfo }) => {
  const { classes } = useStyles();
  const { fields } = useContext(DSContext);

  const entititData = useSelector((state) => state.entititData);
  const { status: enStatus, entities } = entititData;

  const [selectedMappings, setSelectedMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState({
    entity: "",
    field: "",
    type: "",
    length: "",
  });

  const [search, setSearch] = useState("");

  const saveDisabled = useMemo(() => {
    if (!selectedMapping.type) return false;

    return !!(
      currentMapping.fieldType !== selectedMapping.type ||
      (selectedMapping.length && currentMapping.fieldLength && selectedMapping.length < currentMapping.fieldLength)
    );
  }, [currentMapping.fieldType, currentMapping.fieldLength, selectedMapping.type, selectedMapping.length]);

  const onSaveClick = useCallback(() => {
    if (selectedMapping.entity && selectedMapping.field) {
      addMapping({
        entity: selectedMapping.entity,
        field: selectedMapping.field,
      });
    } else {
      addMapping({});
    }
  }, [addMapping, selectedMapping.entity, selectedMapping.field]);

  const onClose = useCallback(() => {
    if (currentMapping.entity && currentMapping.field) {
      addMapping({
        entity: currentMapping.entity,
        field: currentMapping.field,
      });
    } else {
      addMapping({});
    }
  }, [addMapping, currentMapping.entity, currentMapping.field]);

  useEffect(() => {
    if (enStatus === "notFetched") fetchENTRequiredInfo();
  }, [enStatus, fetchENTRequiredInfo]);

  useEffect(() => {
    //Getting type and length
    if (currentMapping?.entity) {
      const mappedEntity = entities.find((_entity) => _entity.name === currentMapping.entity);
      if (!mappedEntity) return;
      const mappedField = mappedEntity.fields.find((_field) => _field.name === currentMapping.field);

      if (mappedField) {
        setSelectedMapping({
          entity: currentMapping.entity,
          field: currentMapping.field,
          type: mappedField.type,
          length: mappedField.length,
        });
      }
    }
  }, [entities, currentMapping]);

  useEffect(() => {
    const tSelectedMappings = [];

    fields.forEach((field) => {
      if (
        field?.entity_mapping &&
        Object.keys(field.entity_mapping).length &&
        currentMapping.field !== field.entity_mapping.field &&
        currentMapping.entity !== field.entity_mapping.entity
      )
        tSelectedMappings.push(field.entity_mapping);
    });

    setSelectedMappings(tSelectedMappings);
  }, [currentMapping.entity, currentMapping.field, fields]);

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{ position: "sticky", right: 12, top: 44, margin: 0, padding: 0, zIndex: 10000, backgroundColor: "white" }}
      >
        <Box className={classes.DrawerHeader}>
          <Typography color="primary" sx={{ flexGrow: "1" }}>
            Entity Mapping
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography className={classes.heading} variant="h6" gutterBottom component="div">
          Attribute :
          <span>
            {currentMapping.fieldName} ({currentMapping.fieldType}
            {currentMapping.fieldLength ? ` - ${currentMapping.fieldLength}` : ""})
          </span>
        </Typography>
        <Divider />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search attribute"
            variant="outlined"
            InputProps={{
              startAdornment: <SearchIcon color="primary" />,
              ...(search && {
                endAdornment: (
                  <IconButton onClick={() => setSearch("")} size="small" sx={{ p: 0 }}>
                    <ClearIcon color="primary" />
                  </IconButton>
                ),
              }),
            }}
            sx={{ marginX: 2, marginTop: 1, width: "90%" }}
          />
        </Box>
      </Box>
      <Toolbar />

      <Box sx={{ width: 440, padding: "0px 10px 20px" }} role="presentation">
        <MappingList
          currentMapping={currentMapping}
          saveDisabled={saveDisabled}
          selectedMappings={selectedMappings}
          selectedMapping={selectedMapping}
          setSelectedMapping={setSelectedMapping}
          entities={entities}
          search={search}
        />
      </Box>
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          position: "sticky",
          right: 12,
          bottom: 0,
          zIndex: 10000,
          backgroundColor: "white",
          padding: "15px",
          flexGrow: "1",
        }}
      >
        <Box>
          <ButtonUI
            disabled={saveDisabled}
            variant="contained"
            color="success"
            sx={{ mr: "10px" }}
            onClick={onSaveClick}
          >
            Save
          </ButtonUI>
          <ButtonUI variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </ButtonUI>
        </Box>
      </Paper>
    </KMKDrawer>
  );
};

export default React.memo(AddMapping);
